import { useState } from "react";
import { X, AlertTriangle } from "lucide-react";
import type { LeadTier, CompanySize, LeadSource } from "../types";
import { LEAD_TIERS, TIER_EMOJI, COMPANY_SIZES, LEAD_SOURCES, SIZE_LABELS, isTierSizeMismatch, isValidEmail } from "../constants";
import { useLocale } from "../../../context/LanguageContext";

export interface CreateLeadFormData {
  name: string;
  email: string;
  company: string;
  size: CompanySize;
  tier: LeadTier;
  phone?: string;
  notes?: string;
  source: LeadSource;
}

interface CreateLeadDialogProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (data: CreateLeadFormData) => void;
  loading: boolean;
  error?: string | null;
}

export function CreateLeadDialog({
  open,
  onClose,
  onSubmit,
  loading,
  error,
}: CreateLeadDialogProps) {
  const { t } = useLocale();

  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [company, setCompany] = useState("");
  const [size, setSize] = useState<CompanySize>("XS");
  const [tier, setTier] = useState<LeadTier>("SEED");
  const [phone, setPhone] = useState("");
  const [notes, setNotes] = useState("");
  const [source, setSource] = useState<LeadSource>("MANUAL");
  const [touched, setTouched] = useState<Record<string, boolean>>({});

  if (!open) return null;

  const emailValid = email.trim() === "" || isValidEmail(email.trim());
  const tierSizeWarning = isTierSizeMismatch(tier, size);

  const canSubmit =
    name.trim() !== "" &&
    email.trim() !== "" &&
    emailValid &&
    company.trim() !== "" &&
    !loading;

  const handleSubmit = () => {
    if (!canSubmit) {
      // Mark all fields as touched to show validation
      setTouched({ name: true, email: true, company: true });
      return;
    }
    onSubmit({
      name: name.trim(),
      email: email.trim(),
      company: company.trim(),
      size,
      tier,
      phone: phone.trim() || undefined,
      notes: notes.trim() || undefined,
      source,
    });
  };

  const resetAndClose = () => {
    setName("");
    setEmail("");
    setCompany("");
    setSize("XS");
    setTier("SEED");
    setPhone("");
    setNotes("");
    setSource("MANUAL");
    setTouched({});
    onClose();
  };

  return (
    <>
      <div className="fixed inset-0 z-40 bg-black/30" onClick={resetAndClose} />
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <div className="w-full max-w-lg rounded-lg border border-border bg-card shadow-xl">
          {/* Header */}
          <div className="flex items-center justify-between border-b border-border px-6 py-4">
            <h2 className="text-lg font-semibold text-foreground">
              {t("leads.createLead")}
            </h2>
            <button
              type="button"
              onClick={resetAndClose}
              className="rounded-md p-1.5 text-muted-foreground hover:bg-muted hover:text-foreground"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* Body */}
          <div className="space-y-4 px-6 py-5">
            {/* Server error banner */}
            {error && (
              <div className="flex items-start gap-2 rounded-md border border-red-200 bg-red-50 px-3 py-2.5 text-sm text-red-800">
                <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-red-500" />
                <span>{error}</span>
              </div>
            )}

            {/* Name */}
            <div>
              <label className="mb-1 block text-sm font-medium text-foreground">
                {t("leads.form.name")} *
              </label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                onBlur={() => setTouched((p) => ({ ...p, name: true }))}
                maxLength={200}
                className={`w-full rounded-md border bg-background px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 ${
                  touched.name && !name.trim()
                    ? "border-red-400 focus:border-red-400 focus:ring-red-400"
                    : "border-border focus:border-primary focus:ring-primary"
                }`}
                placeholder={t("leads.form.namePlaceholder")}
              />
              {touched.name && !name.trim() && (
                <p className="mt-1 text-xs text-red-600">{t("leads.validation.required")}</p>
              )}
            </div>

            {/* Email */}
            <div>
              <label className="mb-1 block text-sm font-medium text-foreground">
                {t("leads.form.email")} *
              </label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                onBlur={() => setTouched((p) => ({ ...p, email: true }))}
                maxLength={320}
                className={`w-full rounded-md border bg-background px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 ${
                  touched.email && (!email.trim() || !emailValid)
                    ? "border-red-400 focus:border-red-400 focus:ring-red-400"
                    : "border-border focus:border-primary focus:ring-primary"
                }`}
                placeholder={t("leads.form.emailPlaceholder")}
              />
              {touched.email && !email.trim() && (
                <p className="mt-1 text-xs text-red-600">{t("leads.validation.required")}</p>
              )}
              {touched.email && email.trim() && !emailValid && (
                <p className="mt-1 text-xs text-red-600">{t("leads.validation.invalidEmail")}</p>
              )}
            </div>

            {/* Company */}
            <div>
              <label className="mb-1 block text-sm font-medium text-foreground">
                {t("leads.form.company")} *
              </label>
              <input
                type="text"
                value={company}
                onChange={(e) => setCompany(e.target.value)}
                onBlur={() => setTouched((p) => ({ ...p, company: true }))}
                maxLength={200}
                className={`w-full rounded-md border bg-background px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 ${
                  touched.company && !company.trim()
                    ? "border-red-400 focus:border-red-400 focus:ring-red-400"
                    : "border-border focus:border-primary focus:ring-primary"
                }`}
                placeholder={t("leads.form.companyPlaceholder")}
              />
              {touched.company && !company.trim() && (
                <p className="mt-1 text-xs text-red-600">{t("leads.validation.required")}</p>
              )}
            </div>

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              {/* Company Size */}
              <div>
                <label className="mb-1 block text-sm font-medium text-foreground">
                  {t("leads.form.size")}
                </label>
                <select
                  value={size}
                  onChange={(e) => setSize(e.target.value as CompanySize)}
                  className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm text-foreground focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
                >
                  {COMPANY_SIZES.map((s) => (
                    <option key={s} value={s}>
                      {SIZE_LABELS[s]} {t("leads.form.employees")}
                    </option>
                  ))}
                </select>
              </div>

              {/* Tier */}
              <div>
                <label className="mb-1 block text-sm font-medium text-foreground">
                  {t("leads.form.tier")}
                </label>
                <select
                  value={tier}
                  onChange={(e) => setTier(e.target.value as LeadTier)}
                  className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm text-foreground focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
                >
                  {LEAD_TIERS.map((tierOpt) => (
                    <option key={tierOpt} value={tierOpt}>
                      {TIER_EMOJI[tierOpt]} {t(`leads.tier.${tierOpt}`)}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Tier-size mismatch warning */}
            {tierSizeWarning && (
              <div className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
                <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-amber-500" />
                <span>{t("leads.validation.tierSizeMismatch")}</span>
              </div>
            )}

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              {/* Source */}
              <div>
                <label className="mb-1 block text-sm font-medium text-foreground">
                  {t("leads.form.source")}
                </label>
                <select
                  value={source}
                  onChange={(e) => setSource(e.target.value as LeadSource)}
                  className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm text-foreground focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
                >
                  {LEAD_SOURCES.map((s) => (
                    <option key={s} value={s}>
                      {t(`leads.source.${s}`)}
                    </option>
                  ))}
                </select>
              </div>

              {/* Phone */}
              <div>
                <label className="mb-1 block text-sm font-medium text-foreground">
                  {t("leads.form.phone")}
                </label>
                <input
                  type="tel"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  maxLength={50}
                  className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
                  placeholder="+49 ..."
                />
              </div>
            </div>

            {/* Notes */}
            <div>
              <label className="mb-1 block text-sm font-medium text-foreground">
                {t("leads.form.notes")}
              </label>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows={3}
                maxLength={2000}
                className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
                placeholder={t("leads.form.notesPlaceholder")}
              />
            </div>
          </div>

          {/* Footer */}
          <div className="flex justify-end gap-3 border-t border-border px-6 py-4">
            <button
              type="button"
              onClick={resetAndClose}
              className="rounded-md border border-border px-4 py-2 text-sm font-medium text-muted-foreground hover:bg-muted"
            >
              {t("common.cancel")}
            </button>
            <button
              type="button"
              onClick={handleSubmit}
              disabled={!canSubmit}
              className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
            >
              {loading ? t("common.loading") : t("common.create")}
            </button>
          </div>
        </div>
      </div>
    </>
  );
}
